import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight, Thermometer, Droplets, Shield, Zap, FlameKindling, CheckCircle2, Phone, Mail, Download } from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

/* ── 浅色工业风色值 ── */
const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const BORDER = '#D0D5E2';
const MUTED_TEXT = '#5A6780';
const DEEP_BG = '#F0F2F5';

const fadeUp = { hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

/* ── 技术参数数据 ── */
const techParams = [
  { test: '拉伸强度', standard: 'ISO 37-2011 / ASTM D412', value: '7 MPa', note: '标准配方实测值' },
  { test: '断裂伸长率', standard: 'ISO 37-2011 / ASTM D412', value: '200%', note: '标准配方实测值' },
  { test: '硬度', standard: 'ISO 48-2010 / ASTM D2240', value: '70±5 Shore A', note: '可定制硬度区间' },
  { test: '比重', standard: 'ISO 2781-2008', value: '1.95 g/cm³', note: '—' },
  { test: '工作温度范围', standard: '—', value: '-30℃ 至 +220℃', note: '特制配方可至 +250℃' },
  { test: '压缩永久变形率', standard: 'ISO 815 / ASTM D395', value: '≤30%（参考值）', note: '70℃×22h' },
  { test: '耐油浸泡后体积变化', standard: 'ISO 1817 / ASTM D471', value: '<25%（参考值）', note: 'ASTM 3#标准油，70℃×70h' },
  { test: '阻燃性', standard: '—', value: '自熄', note: '固有阻燃，无需添加剂' },
];

/* ── 定制配方对比数据 ── */
const customFormulas = [
  { dim: '工作温度上限', standard: '+220℃', custom: '可评估至 +250℃', use: '高温炉窑周边设备、石化高温管线' },
  { dim: '工作温度下限', standard: '-20℃', custom: '可评估至 -25℃', use: '寒区设备或冷库设施' },
  { dim: '硬度', standard: '70±5 Shore A', custom: '可向 60A 或 80A 调配', use: '较软密封面或较高刚度支撑场合' },
  { dim: '耐特定介质性', standard: '通用耐化学品', custom: '可针对特定溶剂强化', use: '特定化工介质接触场合' },
  { dim: '颜色', standard: '黑 / 绿 / 棕', custom: '可按需协调', use: '有色彩识别要求的场合' },
  { dim: '厚度 / 幅宽', standard: '标准规格', custom: '可定制', use: '异形件、大尺寸板件' },
];

/* ── 定制流程 ── */
const customSteps = [
  { num: '01', title: '说明工况', desc: '提供温度/介质/尺寸要求' },
  { num: '02', title: '技术评估', desc: '技术团队评估配方可行性' },
  { num: '03', title: '试制打样', desc: '确认配方方案安排试制' },
  { num: '04', title: '测试数据', desc: '提供样品及测试数据' },
  { num: '05', title: '量产交付', desc: '客户确认后安排量产' },
];

/* ── 核心特性 ── */
const coreFeatures = [
  {
    icon: Thermometer,
    title: '宽温区耐受',
    titleEn: 'WIDE TEMP RANGE',
    desc: '常规工作温度 -30°C 至 +220°C，特制配方可至 -40°C / +250°C，覆盖几乎所有工业高温场合。',
  },
  {
    icon: Droplets,
    title: '优异耐化学品性',
    titleEn: 'CHEMICAL RESISTANCE',
    desc: '对烃类、脂族、芳香族化合物、氯化溶剂、浓酸、浓碱均具有较强抵抗力，覆盖化工主流介质。',
  },
  {
    icon: Shield,
    title: '耐压缩变形',
    titleEn: 'LOW COMPRESSION SET',
    desc: '持续受压状态下压缩永久变形率低，保持长效密封性能，适用于长期压紧密封场合。',
  },
  {
    icon: Zap,
    title: '抗老化耐臭氧',
    titleEn: 'AGING RESISTANCE',
    desc: '高温富氧或臭氧环境中不易龟裂，长效稳定，户外或苛刻大气条件下使用寿命显著优于通用橡胶。',
  },
  {
    icon: FlameKindling,
    title: '固有阻燃自熄',
    titleEn: 'SELF-EXTINGUISHING',
    desc: '具有良好的固有阻燃特性，通常可自行熄灭，无需额外添加阻燃剂，满足高安全等级场合要求。',
  },
];

/* ── 应用场景 ── */
interface ApplicationItem {
  num: string;
  title: string;
  titleEn: string;
  desc: string;
  details: string[];
  img: string | null;
  imgAlt: string;
}

const applications: ApplicationItem[] = [
  {
    num: '①',
    title: '化工设备密封垫片与衬垫',
    titleEn: 'CHEMICAL SEALING',
    desc: '化工生产中的反应釜、储罐、输送管道、阀门及法兰接口，长期接触酸液、碱液、有机溶剂或腐蚀性气体。FKM 可在 -30°C 至 +220°C 温区内，对绝大多数有机溶剂及强腐蚀性介质保持稳定。',
    details: ['泵体密封垫', '管道法兰垫片', '阀盖衬垫', '储罐人孔密封'],
    // 使用现有库中的化工管道图
    img: IMAGES.P05_CHEMICAL_PIPES_1_171,
    imgAlt: '化工管道密封应用',
  },
  {
    num: '②',
    title: '石油与天然气行业',
    titleEn: 'OIL & GAS',
    desc: '油气处理、炼化及输送设备需同时应对高温、石油基流体及硫化氢等腐蚀性气体的复合考验。FKM 对石油基液压油、原油及燃油的耐受性突出，在井口设备、高压阀门、石化储罐及输送系统中被广泛应用。',
    details: ['井口密封组件', '高压阀门垫片', '石化储罐密封', '炼化管路缓冲垫'],
    // 使用现有库中的炼化厂图
    img: IMAGES.P08_REFINERY_1_172,
    imgAlt: '石油炼化厂应用场景',
  },
  {
    num: '③',
    title: '高温工业设备配套',
    titleEn: 'HIGH-TEMP EQUIPMENT',
    desc: '烘箱、热处理炉、高温压机、热油管道等持续高温工作环境中，FKM 能在高温下保持弹性与密封效果。适用于设备门封条、管路减震垫、高温区域防护衬垫等场合。',
    details: ['炉窑门封条', '高温压机密封', '热油管路减震垫', '高温区防护衬垫'],
    // 使用现有库中的法兰图
    img: IMAGES.P08_FLANGE_1_213,
    imgAlt: '高温工业设备密封',
  },
  {
    num: '④',
    title: '汽车与工程机械领域',
    titleEn: 'AUTO & MACHINERY',
    desc: '涡轮增压器周边的高温密封、发动机舱内与燃油/润滑油接触的密封组件，以及重型工程机械液压系统中耐油耐温要求严苛的部位。在 150°C 以上高温油气混合环境中表现明显优于 EPDM 或 NBR。',
    details: ['涡轮增压器密封', '发动机燃油系统密封', '液压系统密封件', '高温油气管路垫片'],
    img: IMAGES.FKM_AUTOMOTIVE_TURBO_SEAL,
    imgAlt: '汽车与工程机械密封应用',
  },
];

export default function ProductFKM() {
  return (
    <div className="bg-background text-foreground min-h-screen">
      {/* ── 面包屑导航 ── */}
      <div className="border-b border-border" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-xs" style={{ color: MUTED_TEXT }}>
            <Link to="/" className="hover:text-primary transition-colors">首页</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={ROUTE_PATHS.PRODUCTS} className="hover:text-primary transition-colors">产品系列</Link>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: ORANGE }}>FKM 氟橡胶板</span>
          </div>
        </div>
      </div>

      {/* ── Hero 区 ── */}
      <section className="relative overflow-hidden" style={{ background: DEEP_BG, minHeight: 420 }}>
        {/* 背景：使用已有炼化厂图作为Hero背景 */}
        <img
          src={IMAGES.P08_REFINERY_1_172}
          alt="FKM氟橡胶应用场景"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.12 }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(15,30,53,0.90) 0%, rgba(15,30,53,0.65) 100%)' }}
        />
        <div className="relative container mx-auto px-4 py-16 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            {/* 返回按钮 */}
            <motion.div variants={fadeUp} className="mb-8">
              <Link
                to={ROUTE_PATHS.PRODUCTS}
                className="inline-flex items-center gap-2 text-sm transition-colors"
                style={{ color: MUTED_TEXT }}
              >
                <ArrowLeft className="w-4 h-4" />
                返回产品列表
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* 左：文字 */}
              <div>
                <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                  <span
                    className="px-3 py-1 text-xs font-black tracking-[0.2em] uppercase rounded-sm"
                    style={{ background: 'oklch(0.66 0.19 42 / 0.15)', color: ORANGE, border: `1px solid ${ORANGE}` }}
                  >
                    FKM
                  </span>
                  <span className="text-xs tracking-widest uppercase" style={{ color: MUTED_TEXT }}>
                    FLUOROCARBON RUBBER
                  </span>
                </motion.div>

                <motion.h1 variants={fadeUp} className="text-4xl lg:text-5xl font-black mb-4 leading-tight">
                  <span style={{ color: ORANGE }}>FKM</span> 氟橡胶板
                </motion.h1>
                <motion.p variants={fadeUp} className="text-lg font-medium mb-2" style={{ color: 'oklch(0.75 0.010 225)' }}>
                  面对最苛刻化学与温度环境的工业材料首选
                </motion.p>
                <motion.p variants={fadeUp} className="text-sm leading-relaxed mb-8" style={{ color: MUTED_TEXT }}>
                  适用于化工、石化、航空、汽车、食品机械等对耐化学品与耐高温有严苛要求的工业领域。所有数据均源自实验室测试报告与生产质量记录，符合 ISO / ASTM 国际标准体系。
                </motion.p>

                <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                  <Link
                    to={ROUTE_PATHS.CONTACT}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm transition-all"
                    style={{ background: ORANGE, color: '#FFFFFF' }}
                  >
                    <Phone className="w-4 h-4" />
                    立即询价
                  </Link>
                  <Link
                    to={`${ROUTE_PATHS.TECH_SPECS}#fkm`}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm transition-all"
                    style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
                  >
                    查看技术参数
                  </Link>
                </motion.div>
              </div>

              {/* 右：产品图 + 规格卡 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-4">
                {/* 产品实物图 - 使用黑色橡胶板+钢尺图 */}
                <div
                  className="rounded overflow-hidden"
                  style={{ border: `1px solid ${BORDER}` }}
                >
                  <img
                    src={IMAGES.P05_FKM_SHEETS_1_199}
                    alt="FKM氟橡胶板材实物"
                    className="w-full object-cover"
                    style={{ maxHeight: 260, objectPosition: 'center' }}
                  />
                </div>

                {/* 快速规格 */}
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: '工作温度', value: '-30°C ~ +220°C', sub: '特制可至+250°C' },
                    { label: '标准硬度', value: '70±5 Shore A', sub: '可定制 60A/80A' },
                    { label: '板材厚度', value: '1.5 ~ 20.0mm', sub: '幅宽 0.91m ~ 2m' },
                  ].map((s) => (
                    <div key={s.label} className="rounded p-3 text-center" style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}>
                      <p className="text-[10px] tracking-widest uppercase mb-1" style={{ color: MUTED_TEXT }}>{s.label}</p>
                      <p className="text-sm font-bold leading-tight" style={{ color: ORANGE }}>{s.value}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: MUTED_TEXT }}>{s.sub}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 核心特性 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CORE PROPERTIES</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">核心特性</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {coreFeatures.map((f) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    variants={fadeUp}
                    className="rounded p-5 flex flex-col gap-3"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  >
                    <div className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
                      style={{ background: 'oklch(0.66 0.19 42 / 0.12)', border: `1px solid oklch(0.66 0.19 42 / 0.3)` }}>
                      <Icon className="w-5 h-5" style={{ color: ORANGE }} />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold tracking-widest uppercase mb-0.5" style={{ color: MUTED_TEXT }}>{f.titleEn}</p>
                      <p className="text-sm font-bold mb-2">{f.title}</p>
                      <p className="text-xs leading-relaxed" style={{ color: MUTED_TEXT }}>{f.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 技术参数 ── */}
      <section id="tech-params" className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>TECHNICAL DATA</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">FKM 实测物理性能数据</h2>
              <p className="text-sm text-center mt-2" style={{ color: MUTED_TEXT }}>
                所有数值均为标准配方典型实测值，依据 ISO / ASTM 国际标准体系测试
              </p>
            </motion.div>

            {/* 核心指标高亮 */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: '拉伸强度', value: '7 MPa', sub: 'ISO 37 / ASTM D412' },
                { label: '断裂伸长率', value: '200%', sub: 'ISO 37 / ASTM D412' },
                { label: '标准硬度', value: '70 Shore A', sub: 'ISO 48 / ASTM D2240' },
                { label: '最高工作温度', value: '+220°C', sub: '特制配方可至 +250°C' },
              ].map((m) => (
                <div
                  key={m.label}
                  className="rounded p-5 text-center"
                  style={{ background: CARD_BG, border: `1px solid oklch(0.66 0.19 42 / 0.25)` }}
                >
                  <p className="text-3xl font-black mb-1" style={{ color: ORANGE }}>{m.value}</p>
                  <p className="text-sm font-semibold mb-1">{m.label}</p>
                  <p className="text-[10px] font-mono" style={{ color: MUTED_TEXT }}>{m.sub}</p>
                </div>
              ))}
            </motion.div>

            {/* 完整参数表 */}
            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['测试项目', '测试标准', '典型数值', '参考说明'].map((h) => (
                      <th
                        key={h}
                        className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase"
                        style={{ color: ORANGE }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {techParams.map((row, i) => (
                    <tr
                      key={row.test}
                      style={{
                        background: i % 2 === 0 ? CARD_BG : '#F0F2F5',
                        borderBottom: `1px solid ${BORDER}`,
                      }}
                    >
                      <td className="px-5 py-3.5 font-semibold">{row.test}</td>
                      <td className="px-5 py-3.5 font-mono text-xs" style={{ color: MUTED_TEXT }}>{row.standard}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.value}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-5 flex items-start gap-2 text-xs" style={{ color: MUTED_TEXT }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ORANGE }} />
              <span>适用国际测试体系：ISO（国际标准化组织）、ASTM（美国材料与试验协会），依据应标准提供检测数据，满足进口商合规要求。具体项目请索取完整 TDS 技术数据表。</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 典型应用场景 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-12">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>FKM APPLICATIONS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">经过验证的主要应用领域</h2>
            </motion.div>

            <div className="space-y-8">
              {applications.map((app, idx) => (
                <motion.div
                  key={app.num}
                  variants={fadeUp}
                  className="rounded overflow-hidden"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 ${idx % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                    {/* 图片列 */}
                    <div
                      className={`relative overflow-hidden ${idx % 2 === 1 ? 'lg:order-2' : ''}`}
                      style={{ minHeight: 280 }}
                    >
                      {app.img ? (
                        <img
                          src={app.img}
                          alt={app.imgAlt}
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ opacity: 0.85 }}
                        />
                      ) : (
                        /* 无匹配图片时的占位区 */
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center gap-3"
                          style={{ background: '#ECEEF2', border: `2px dashed ${BORDER}` }}
                        >
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'oklch(0.66 0.19 42 / 0.1)' }}>
                            <span className="text-2xl" style={{ color: ORANGE }}>④</span>
                          </div>
                          <p className="text-xs font-mono text-center px-6 leading-relaxed" style={{ color: MUTED_TEXT }}>
                            图片位置保留<br />
                            <span className="text-[10px]">汽车发动机/涡轮增压器密封场景</span>
                          </p>
                        </div>
                      )}
                      {/* 应用编号角标 */}
                      <div
                        className="absolute top-4 left-4 w-10 h-10 rounded-sm flex items-center justify-center font-black text-lg z-10"
                        style={{ background: ORANGE, color: '#FFFFFF' }}
                      >
                        {app.num}
                      </div>
                    </div>

                    {/* 内容列 */}
                    <div className={`p-8 flex flex-col justify-center ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <p className="text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: ORANGE }}>{app.titleEn}</p>
                      <h3 className="text-xl font-black mb-4">{app.title}</h3>
                      <p className="text-sm leading-relaxed mb-6" style={{ color: MUTED_TEXT }}>{app.desc}</p>

                      <div>
                        <p className="text-xs font-bold tracking-wider uppercase mb-3" style={{ color: MUTED_TEXT }}>典型用途</p>
                        <div className="grid grid-cols-2 gap-2">
                          {app.details.map((d) => (
                            <div key={d} className="flex items-center gap-2 text-xs">
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                              {d}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 定制配方能力 ── */}
      <section className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CUSTOM FORMULATION</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">FKM 定制配方能力说明</h2>
              <p className="text-sm text-center mt-2 max-w-2xl mx-auto" style={{ color: MUTED_TEXT }}>
                FKM 标准配方已能覆盖大多数化工与高温工况。针对特殊需求，可在以下维度进行配方调整。
                所有调整均需技术评估后安排试制打样，以实测数据为批量生产依据。
              </p>
            </motion.div>

            {/* 定制维度表 */}
            <motion.div variants={fadeUp} className="overflow-x-auto rounded mb-12" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['调整维度', '标准配方典型值', '定制调整方向', '适用场景参考'].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {customFormulas.map((row, i) => (
                    <tr key={row.dim} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                      <td className="px-5 py-3.5 font-semibold">{row.dim}</td>
                      <td className="px-5 py-3.5 font-mono text-xs" style={{ color: 'oklch(0.78 0.012 225)' }}>{row.standard}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.custom}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.use}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>

            {/* 定制流程 */}
            <motion.div variants={fadeUp}>
              <h3 className="text-base font-bold text-center mb-6" style={{ color: MUTED_TEXT }}>定制流程说明</h3>
              <div className="flex flex-col lg:flex-row items-stretch gap-0">
                {customSteps.map((step, i) => (
                  <>
                    {/* 步骤卡片：每个等宽，flex-1 */}
                    <div
                      key={step.num}
                      className="flex-1 rounded p-5 text-center"
                      style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                    >
                      <div
                        className="w-10 h-10 rounded-sm flex items-center justify-center font-black text-sm mx-auto mb-3"
                        style={{ background: 'oklch(0.66 0.19 42 / 0.15)', color: ORANGE, border: `1px solid oklch(0.66 0.19 42 / 0.4)` }}
                      >
                        {step.num}
                      </div>
                      <p className="text-sm font-bold mb-1">{step.title}</p>
                      <p className="text-xs" style={{ color: MUTED_TEXT }}>{step.desc}</p>
                    </div>
                    {/* 箭头：平级，不占 flex-1 */}
                    {i < customSteps.length - 1 && (
                      <div key={`arrow-${i}`} className="flex items-center justify-center w-full lg:w-6 py-2 lg:py-0 flex-shrink-0">
                        <ChevronRight
                          className="w-5 h-5 rotate-90 lg:rotate-0"
                          style={{ color: MUTED_TEXT }}
                        />
                      </div>
                    )}
                  </>
                ))}
              </div>
              <p className="text-xs text-center mt-5 italic" style={{ color: MUTED_TEXT }}>
                注：所有定制配方调整均需经技术团队评估可行性，确认后安排试制打样，以样品实测数据为批量生产依据。
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 生产工艺简介 ── */}
      <section className="py-16" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* FKM截面图 */}
              <motion.div variants={fadeUp} className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                <img
                  src={IMAGES.P06_FKM_SECTION_1_1_192}
                  alt="FKM氟橡胶板材截面结构"
                  className="w-full object-cover"
                  style={{ maxHeight: 320 }}
                />
              </motion.div>

              <motion.div variants={fadeUp}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                  <div>
                    <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: ORANGE }}>MANUFACTURING PROCESS</p>
                    <h2 className="text-xl font-black">生产工艺</h2>
                  </div>
                </div>
                <p className="text-sm leading-relaxed mb-6" style={{ color: MUTED_TEXT }}>
                  采用密炼机对氟橡胶母胶与各类助剂进行精确配混，通过硫化压机在受控温度与压力下完成板材硫化成型，
                  硫化参数依据胶料特性逐批设定，确保批次间性能一致。
                  板材出厂前经拉力机与硬度计进行出厂检测，不达标批次不予放行。
                </p>

                <div className="space-y-3">
                  {[
                    { label: '颜色规格', value: '黑色 / 绿色 / 棕色' },
                    { label: '厚度范围', value: '1.5mm 至 20.0mm' },
                    { label: '幅宽', value: '0.91m 至 2m（标准）' },
                    { label: '定制', value: '可按客户要求提供定制尺寸或裁切' },
                  ].map((spec) => (
                    <div key={spec.label} className="flex items-center gap-4 text-sm py-2.5" style={{ borderBottom: `1px solid ${BORDER}` }}>
                      <span className="w-24 flex-shrink-0 text-xs font-bold tracking-wider uppercase" style={{ color: MUTED_TEXT }}>
                        {spec.label}
                      </span>
                      <span className="font-semibold">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14" style={{ background: DEEP_BG, borderTop: `1px solid ${BORDER}` }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.div variants={fadeUp} className="mb-4">
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: ORANGE }}>GET A QUOTE</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-2xl lg:text-3xl font-black mb-4">
              <>需要 FKM 氟橡胶板材？<br />我们提供技术选型与样品支持</>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-sm mb-8" style={{ color: MUTED_TEXT }}>
              说明您的工况（耐热温度、接触介质、规格尺寸），技术顾问将为您提供选型建议并安排样品。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-black rounded-sm"
                style={{ background: ORANGE, color: '#FFFFFF' }}
              >
                <Mail className="w-4 h-4" />
                提交询价
              </Link>
              <a
                href="tel:13426031477"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm transition-all"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Phone className="w-4 h-4" />
                13426031477
              </a>
              <Link
                to={ROUTE_PATHS.SELECTION}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm transition-all"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Download className="w-4 h-4" />
                查看选型矩阵
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
